import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated || user?.role === 'artist' || user?.role === 'admin' || user?.role === 'agent') {
      setCart({ items: [], total: 0, item_count: 0 });
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCart(response.data);
    } catch (error) {
      setCart({ items: [], total: 0, item_count: 0 });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (artistId, message, agentId = null, songTitle = '') => {
    try {
      setLoading(true);
      const payload = { artist_id: artistId, message };
      if (agentId) payload.agent_id = agentId;
      if (songTitle) payload.song_title = songTitle;
      const response = await api.post('/cart/add', payload);
      setCart(response.data);
      return { success: true };
    } catch (error) {

      return { success: false, error: error.response?.data?.detail || 'Failed to add to cart' };
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, message) => {
    try {
      setLoading(true);
      const response = await api.put(`/cart/item/${itemId}`, { message });
      setCart(response.data);
      return { success: true };
    } catch (error) {

      return { success: false, error: error.response?.data?.detail || 'Failed to update item' };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      const response = await api.delete(`/cart/item/${itemId}`);
      setCart(response.data);
      return { success: true };
    } catch (error) {

      return { success: false, error: error.response?.data?.detail || 'Failed to remove item' };
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      const response = await api.delete('/cart/clear');
      setCart(response.data);
      return { success: true };
    } catch (error) {

      return { success: false, error: error.response?.data?.detail || 'Failed to clear cart' };
    } finally {
      setLoading(false);
    }
  };

  const checkout = async () => {
    try {
      setLoading(true);
      const response = await api.post('/cart/checkout');
      return { success: true, data: response.data };
    } catch (error) {

      return { success: false, error: error.response?.data?.detail || 'Failed to checkout' };
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      checkout,
      refreshCart: fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};
